import { AlertTriangle, CircleAlert, CircleCheck, Info, X } from "lucide-react";
import { useMemo, useState } from "react";

type StatusAlertProps = {
  variant?: "warning" | "danger" | "success" | "info";
  message: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
};

const variants = {
  warning: {
    Icon: AlertTriangle,
    container: "border-theme-warning/40 bg-theme-warning-soft/80 text-theme-warning-text",
    iconWrap: "bg-theme-warning-soft text-theme-warning",
    titleColor: "text-theme-warning-text",
  },
  danger: {
    Icon: CircleAlert,
    container: "border-red-200 bg-red-50/90 text-red-800",
    iconWrap: "bg-red-100 text-red-600",
    titleColor: "text-red-900",
  },
  success: {
    Icon: CircleCheck,
    container: "border-emerald-200 bg-emerald-50/90 text-emerald-800",
    iconWrap: "bg-emerald-100 text-emerald-600",
    titleColor: "text-emerald-900",
  },
  info: {
    Icon: Info,
    container: "border-blue-200 bg-blue-50/90 text-blue-800",
    iconWrap: "bg-blue-100 text-blue-600",
    titleColor: "text-blue-900",
  },
};

export const StatusAlert = ({
  variant = "info",
  message,
  dismissible = false,
  onDismiss,
  className,
}: StatusAlertProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const currentVariant = variants[variant];

  if (!isVisible) {
    return null;
  }

  return (
    <div
      role={variant === "danger" || variant === "warning" ? "alert" : "status"}
      aria-live={variant === "danger" || variant === "warning" ? "assertive" : "polite"}
      className={`group relative overflow-hidden rounded-xl border px-4 py-1 shadow-sm transition-all duration-300 ease-out animate-in fade-in slide-in-from-top-1 ${currentVariant.container} ${className || ""}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${currentVariant.iconWrap}`}
          aria-hidden="true"
        >
          <currentVariant.Icon className="h-4.5 w-4.5" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="mt-1 text-sm leading-6 font-medium">{message}</p>
        </div>

        {dismissible ? (
          <button
            type="button"
            onClick={() => {
              setIsVisible(false);
              onDismiss?.();
            }}
            aria-label="Close"
            className="cursor-pointer text-gray-400 transition-all duration-200 ease-in-out hover:text-theme-danger hover:scale-110 hover:rotate-90 active:scale-95 mt-1"
          >
            <X className="h-6 w-6" />
          </button>
        ) : null}
      </div>
    </div>
  );
};
